import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { CatalogoMonete } from '@/components/limulo/LimuloMontepremi';
import { useLimuloGameName } from '@/hooks/useLimuloGameName';
import { instantShort } from '@/lib/limulo/dateDisplay';
import {
  gameModeDisplay,
  haChiaveGuid,
  normalizeInstant,
  participantsLabel,
  prizeHeadlineOf,
  testoDellaParola,
  tournamentGameRef,
  tournamentTitle,
} from '@/lib/limulo/model';
import { badgeDellaCard } from '@/lib/limulo/quattroTab';
import { limuloTournamentUrl } from '@/lib/limulo/routes';
import type { LimuloTournament } from '@/types/limulo';

/**
 * La riga di un torneo — reference A1, approvata da Luca il 2026-09-20 (I-92).
 *
 * ⚠️ **QUESTA RIGA LA USANO CINQUE SCHERMATE**: la lista (A1, anche nella Hub → EVENTI), «le mie
 * iscrizioni» (A2), lo storico (A3), la striscia «Tornei backgammon» e la scheda «Tornei» della lobby
 * backgammon. Disegnarla una volta si propaga su tutte — **e romperla anche**.
 *
 * Cosa cambia rispetto a prima: c'era il titolo piu' **cinque etichette in fila tutte dello
 * stesso peso** e nessuna immagine. Adesso i dati hanno due ruoli diversi — *cosa e'* e
 * *quanto e quando* — perche' sono due domande diverse e in fila sembravano la stessa.
 *
 * 🔄 **IL CAMPO GIOCO: DAL 26/09 E' IL NOME, NON PIU' IL CODICE GREZZO.** Qui c'era scritto che il
 * campo era un passthrough (vincolo kb#81), perche' risolvere `gameId` contro il catalogo
 * «inventerebbe un legame che il contratto non dichiara». Il legame invece e' in esercizio dal 23/09:
 * la lobby backgammon filtra i tornei proprio con `gameId` → `GET /v1/games` → `profileType`
 * (`lib/qgammon/lobbyTornei.ts`). A schermo il codice diceva «1» per Clash Royale e «100» per il
 * backgammon (censimento del 26/09, riga 2). ⇒ `useLimuloGameName` (#406, efootball-orcb) legge la
 * STESSA query del catalogo. ⚠️ **Il ripiego resta il codice grezzo**, mai «Sconosciuto»: se il
 * catalogo non risponde o il gioco non c'e', la riga dice cio' che il BFF ha mandato, come prima.
 * ⚠️ Il carattere (monospazio) resta quello di prima: lo stile della riga e' di CriGio (kb#294).
 *
 * ⚠️ **Niente `now`**: la card non calcola nessuna finestra. Lo stato d'iscrizione lo dichiara
 * il server (`registrationStateOf`) e non dipende dall'orologio del telefono — che, fra
 * l'altro, e' un secondo modo di dissentire dal backend.
 */
export function LimuloTournamentCard({ tournament }: { tournament: LimuloTournament }) {
  const { t, i18n } = useTranslation('limulo');
  const participants = participantsLabel(tournament);
  const game = tournamentGameRef(tournament);
  const { name: nomeGioco } = useLimuloGameName(game);
  const mode = gameModeDisplay(tournament.gameMode);

  /**
   * *Quando*: l'inizio se c'e', altrimenti l'apertura delle iscrizioni.
   *
   * ⚠️ **E si dice QUALE dei due e'.** Una data nuda accanto a un torneo e' ambigua — «18
   * set» puo' essere il giorno in cui si gioca o quello in cui ci si iscrive, e sono due
   * decisioni diverse per chi legge. L'etichetta costa due parole e toglie l'ambiguita'.
   * ⚠️ Ogni istante passa da `normalizeInstant`: il provider nativo manda **numeri**, e
   * `Date.parse` su un numero da' `NaN`.
   */
  const inizio = normalizeInstant(tournament.schedule?.startAt);
  const apertura = normalizeInstant(tournament.schedule?.registrationOpenAt);
  const quando =
    inizio !== null
      ? { label: t('card.startsAt'), value: instantShort(inizio, i18n.language) }
      : apertura !== null
        ? { label: t('card.opensAt'), value: instantShort(apertura, i18n.language) }
        : null;

  return (
    <Link
      // 🔴 `tournamentId`, NON `id` — dimostrato per esperimento il 2026-09-05 sulla
      // superficie di collaudo, chiamando le cinque rotte con l'uno e con l'altro:
      //
      //   con `id`  (`6a9c2dc3…`, l'ObjectId)      con `tournamentId` (`ciaocup-ca6975b3`)
      //     /tournaments/:id            200            200
      //     …/matches                   200            200
      //     …/registration              424            200
      //     …/registration-requirements 424            200
      //     …/standings                 424            200
      //
      // La causa sta dall'altra parte e non e' nostra: solo DUE dei cinque resolver di
      // `app-qtournaments` accettano un `_id` (`tornei.ts` e `incontri.ts` chiamano
      // `isObjectIdShape`; `iscrizioni.ts`, `classifica.ts` e `ponte.ts` no). Con un `_id`
      // gli altri fanno «non trovato», e quel rifiuto esce come `424 provider non
      // disponibile` — cioe' lo schermo diceva «questo torneo non e' disponibile» di un
      // torneo aperto, coi suoi dati sopra.
      //
      // ⚠️ `tournamentId` funziona su TUTTE E CINQUE: e' l'unico identificatore che vale
      // ovunque, quindi e' l'unico da mettere in un URL.
      //
      // Il fallback su `id` resta perche' un link rotto e' peggio di un link degradato —
      // ma e' un ripiego dichiarato, non un'equivalenza: se ci finisci, le tre rotte qui
      // sopra torneranno 424.
      to={limuloTournamentUrl(tournament.tournamentId || tournament.id)}
      // I-317 (a), 2026-10-03: `p-2.5` (era `p-3`) e `gap-x-1.5` (era `gap-x-2`) nelle due righe meta. SOLO gli spazi
      // interni: la forma e' di CriGio (kb#294), avvisato prima del codice. Con 4 px in meno fra le voci la riga
      // «iscritti · Inizio · Premio» va a capo meno spesso — ma la cifra dipende dai DATI: 2 card su 8 invece di 6 a 360
      // in italiano vale per i dati FINTI della misura (`misura-densita.mjs`, KB 2026-10-03-lista-tornei-proposte.md).
      // 🔴 Corretto il 2026-10-03: su stg-tma, coi dati veri (premi come «10 QCOLLAUDO», «Iscrizioni dal …»), a 360 va a
      // capo su 8 card su 8 [M consegna-tornei-orcb, 13:2xZ]. La classe resta: era la cifra a promettere troppo.
      className="card-edge flex gap-3 rounded-xl bg-bg-secondary p-2.5"
    >
      {/*
        🔴 **LA THUMB, E IL RAMO CHE SI VEDRA' PER PRIMO E' QUELLO VUOTO.**
        Sorgente `tournament.iconUrl`, **piatto al livello alto**: `content?.iconUrl` e' solo
        la fonte di ripiego dentro il sanitizer (`policy.js:246`), non la forma sul filo —
        leggerlo darebbe `undefined` su ogni torneo, e la thumb non comparirebbe mai **senza
        nessun errore** [M 2026-09-20 su `qlash-limulo@origin/staging` = `ca8719b`].
        ⚠️ Il contratto prevede l'immagine su cinque livelli e il modulo dell'organizzatore la
        dichiara, ma **nessuno ha misurato se i tornei vivi l'abbiano popolata** — serve una
        lettura autenticata. ⇒ la stringa vuota e' la norma, non l'eccezione.
        ⛔ **Nessuna iniziale del titolo, nessun logo del gioco, nessun colore derivato
        dall'id**: sarebbero tutti dati INVENTATI al posto di un campo vuoto. Resta il fondo di
        superficie e un segno appena accennato, che non afferma niente.
      */}
      <div className="h-[58px] w-[58px] shrink-0 overflow-hidden rounded-xl bg-bg-tertiary">
        {tournament.iconUrl ? (
          <img
            src={tournament.iconUrl}
            alt=""
            className="h-full w-full object-cover"
            loading="lazy"
          />
        ) : (
          <div aria-hidden className="flex h-full w-full items-center justify-center">
            <span className="h-5 w-5 rounded-md border border-fg-muted/25" />
          </div>
        )}
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex items-start justify-between gap-2">
          <span className="min-w-0 flex-1 truncate text-sm font-bold text-fg">
            {tournamentTitle(tournament)}
          </span>
          {/* 🔴 **LO STATO E' UN BADGE, e viene dal SERVER** — mai dal calendario. Il 06/09 questa riga mostrava «Aperte» su
              un torneo che il server dichiarava `scheduled`, e la card e' il posto da cui si entra: cioe' l'invito a premere.
              ⚠️ Non e' piu' una delle etichette in fila: era la meno visibile ed era la piu' decisiva.
              🔄 Dal 04/10 (I-346) dice la cosa che conta per la tab in cui sta la riga (`badgeDellaCard`): la finestra
              d'iscrizione per ciò che deve ancora partire, lo stato del TORNEO per ciò che e' in corso o passato («Concluso»,
              «Annullato» invece di un «Chiuse» uguale per tutti), «Ritirato» sulle mie righe da cui sono uscito. */}
          <span className="shrink-0 rounded-md bg-bg-tertiary px-2 py-0.5 text-[10px] font-semibold text-fg-muted">
            {testoDellaParola(badgeDellaCard(tournament, Date.now()), t)}
          </span>
        </div>

        {/* PRIMA RIGA — *cosa e'*: il gioco (il nome dal catalogo, o il codice se manca) e la
            modalita'. */}
        <div className="flex flex-wrap items-center gap-x-1.5 text-[11px] text-fg-muted">
          {/* Il gioco compare solo se il BFF l'ha mandato: uno «Sconosciuto» al suo posto
              sarebbe un'informazione che nessuno ci ha dato. */}
          {game ? <span className="font-mono">{nomeGioco}</span> : null}
          {game && mode.text ? <span aria-hidden>·</span> : null}
          {/* ⚠️ `gameMode.label` PUO' essere la chiave macchina (`policy.js:251-262`): si
              tenta la traduzione e si ripiega su cio' che il server ha mandato — senza
              buttare un'etichetta vera quando per una volta arriva.
              🔄 Dal 26/09 (L-2301) la traduzione c'e' per le chiavi NOTE (`LIMULO_GAME_MODE_COPY`:
              «regular» ⇒ ~~«1 contro 1»~~ «1v1», la stessa grafia della coda 1v1 della Hub ma su una
              chiave sua, per la revisione di #453), e il resto resta grezzo come prima: la stessa regola, e la
              stessa funzione, degli stati nella scheda.
              🔄 Dal 30/09 (L-2543 b) anche le 9 del backgammon (`LIMULO_BACKGAMMON_MODE_COPY`): «Match a 3
              punti · rapid», lunghezza e durata. Resta grezzo cio' che la TMA non conosce. */}
          {mode.text ? <span>{testoDellaParola(mode, t)}</span> : null}
        </div>

        {/* SECONDA RIGA — *quanto e quando*. Numeri con `tabular-nums`, o «12/64» e «9/64»
            ballano di un paio di pixel fra un render e l'altro. */}
        <div className="flex flex-wrap items-center gap-x-1.5 text-[11px] text-fg">
          {participants ? (
            <span className="font-semibold tabular-nums">{participants}</span>
          ) : null}
          {participants && quando ? <span aria-hidden className="text-fg-muted">·</span> : null}
          {/* ⚠️ Nessuna riga quando nessuna delle due date e' dichiarata: un «—» affermerebbe
              di aver chiesto e non aver ricevuto, che e' un'informazione diversa. */}
          {quando ? (
            <span className="tabular-nums text-fg-muted">
              {quando.label} {quando.value}
            </span>
          ) : null}
          {/* 🔴 **IL MONTEPREMI STA SULLA RIGA CONDIVISA, e compare ovunque la riga compare**:
              lista, mie iscrizioni, storico, lobby backgammon. E' voluto — Luca, 23/09 17:4xZ,
              «aggiung montepremi». I due vuoti restano DUE (`prizeHeadlineOf`): «non
              dichiarato» e «nessuno» non si fondono in un trattino. */}
          {participants || quando ? <span aria-hidden className="text-fg-muted">·</span> : null}
          {/* 🔴 Con una chiave `currencyId` (ADR-0187) la moneta si risolve dal catalogo: `CatalogoMonete` lo
              legge SOLO se questo torneo ha una chiave GUID, e RTK deduplica la richiesta (cache lunga) —
              una chiamata sola anche per un elenco di venti schede. Senza GUID nessuna chiamata. */}
          <CatalogoMonete serve={haChiaveGuid(tournament)}>
            {(catalogo) => {
              const premio = prizeHeadlineOf(tournament, t, i18n.language, catalogo);
              return (
                <span
                  data-testid="riga-montepremi"
                  className={premio.muted ? 'text-fg-muted' : 'font-semibold tabular-nums'}
                >
                  {t('detail.prize')} {premio.value}
                </span>
              );
            }}
          </CatalogoMonete>
        </div>
      </div>
    </Link>
  );
}
